import {TableCell, TableRow} from '@mui/material';
import React, {FC, ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {BillieInteractionDto} from '../../../../api';
import {Progress} from '../../../../components/Loader';
import {StickyHeaderTable} from '../../../../components/StickyHeaderTable';
import {useGetBillieInteractions} from '../../service';
import {BaseApplication} from '../../model';
import {isMainProfiCardApplication} from '../utils';
import {RetryBillieButton} from '../../ActionButtons';

interface BillieInteractionSectionProps {
    application: BaseApplication;
    additionalRow?: ReactNode;
    showSnackbar?: (params: { message: string; severity: string }) => void;
}

export const BillieInteractionSection: FC<BillieInteractionSectionProps> = ({
                                                                                application,
                                                                                additionalRow
                                                                            }) => {
    const {billieInteractions, ...billieQuery} = useGetBillieInteractions(application.applicationNumber!!);
    const {t} = useTranslation();

    if (!isMainProfiCardApplication(application)) {
        throw new Error('Application is not of type Main Card');
    }

    if (!billieQuery.isFetched) {
        return <Progress size={28}/>;
    }

    if (!Array.isArray(billieInteractions) || billieInteractions.length === 0) {
        console.log('status: ' + application.processingErrorReason, application.status);
        return (
                <StickyHeaderTable headerRow={
                    <TableRow>
                        <TableCell colSpan={5}>
                            {t('application.billie.interaction.noData')}
                        </TableCell>
                    </TableRow>
                }>
                    <></>
                </StickyHeaderTable>
        );
    }

    const sortedInteractions = [...(billieInteractions ?? [])].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
    return (
            <BillieInteractionResultsTable
                    billieInteractions={sortedInteractions}
                    additionalRow={additionalRow}
                    application={application}
                    refresh={billieQuery.refetch}
            />
    );
};

interface BillieInteractionResultsTableProps {
    billieInteractions: BillieInteractionDto[];
    additionalRow?: ReactNode;
    application: BaseApplication;
    refresh: () => void;
}

const BillieInteractionResultsTable: FC<BillieInteractionResultsTableProps> = ({
                                                                                   billieInteractions,
                                                                                   additionalRow,
                                                                                   application,
                                                                                   refresh,
                                                                               }) => {
    const {t} = useTranslation();
    const formatMoney = (money: any) => {
        if (!money) return '';
        if (typeof money === 'string') return money;
        if (typeof money === 'number') return money.toString();
        if (money.amount !== undefined && money.currency !== undefined) {
            return `${money.amount} ${money.currency}`;
        }
        return String(money);
    };
    return (
            <StickyHeaderTable headerRow={
                <TableRow>
                    <TableCell>{t('application.billie.interaction.header.id')}</TableCell>
                    <TableCell>{t('application.billie.interaction.header.crefoId')}</TableCell>
                    <TableCell>{t('application.billie.interaction.header.timestamp')}</TableCell>
                    <TableCell>{t('application.billie.interaction.header.companyName')}</TableCell>
                    <TableCell>{t('application.billie.interaction.header.companyAddress')}</TableCell>
                    <TableCell>{t('application.billie.interaction.header.matrixLimit')}</TableCell>
                    <TableCell>{t('application.billie.interaction.header.condition')}</TableCell>
                    <TableCell>{t('application.billie.interaction.header.reason')}</TableCell>
                </TableRow>
            }>
                {billieInteractions.map((billieInteraction) => (
                        <TableRow key={billieInteraction.id}>
                            <TableCell>{billieInteraction.id}</TableCell>
                            <TableCell>{billieInteraction.crefoId}</TableCell>
                            <TableCell>
                                {billieInteraction.timestamp
                                        ? new Date(billieInteraction.timestamp).toLocaleString('de-DE')
                                        : ''}
                            </TableCell>
                            <TableCell>{billieInteraction.companyName}</TableCell>
                            <TableCell>{billieInteraction.companyAddress?.street} {billieInteraction.companyAddress?.houseNumber}, {billieInteraction.companyAddress?.zipCode} {billieInteraction.companyAddress?.city} </TableCell>
                            <TableCell>{formatMoney(billieInteraction.matrixLimit)}</TableCell>
                            <TableCell>{t(`application.billie.interaction.condition.${billieInteraction.risk}`, { defaultValue: billieInteraction.risk ?? '' })}</TableCell>
                            <TableCell>{t(`application.billie.interaction.billieResult.${billieInteraction.reason}`, { defaultValue: billieInteraction.reason ?? '' })}</TableCell>
                        </TableRow>
                ))}
                {additionalRow}
                {application.status === 'NEEDS_MANUAL_INTERVENTION' &&
                        application.processingErrorReason === 'CREDIT_INSTITUTION' && (
                                <TableRow>
                                    <TableCell colSpan={8}>
                                        <RetryBillieButton application={application} refresh={refresh}/>
                                    </TableCell>
                                </TableRow>
                        )}
            </StickyHeaderTable>
    );
};
